import { useEffect, useMemo, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

import {
  useCreatorInviteDetail,
  useMarkInviteSent,
  useUpdateCreatorInvite,
} from './useCreators';
import { CreatorMvpNote } from './CreatorMvpNote';
import type { CreatorInvite, OutreachChannel } from './types';

const INVITE_BENEFIT_COUNT = 3;
const INVITE_HEADLINE_MAX = 72;
const INVITE_SUBTITLE_MAX = 180;
const INVITE_CTA_MAX = 40;
const INVITE_BULLET_TITLE_MAX = 32;
const INVITE_BULLET_MAX = 92;
const DEFAULT_INVITE_BENEFITS = [
  {
    title: 'Fantasy With A Twist',
    body: 'Every fan gets an AI manager to debate picks, captain calls, and fixtures.',
  },
  {
    title: 'Creator Competition',
    body: 'Your audience joins your competition and tries to beat you and each other.',
  },
  {
    title: 'Built For Content',
    body: 'Turn squad choices, bad calls, and AI advice into posts, polls, and debates.',
  },
];

function benefitsFromParts(titles?: string[] | null, bodies?: string[] | null) {
  return Array.from({ length: INVITE_BENEFIT_COUNT }, (_, index) => ({
    title: (titles?.[index] ?? DEFAULT_INVITE_BENEFITS[index]?.title ?? '').slice(
      0,
      INVITE_BULLET_TITLE_MAX,
    ),
    body: (bodies?.[index] ?? DEFAULT_INVITE_BENEFITS[index]?.body ?? '').slice(
      0,
      INVITE_BULLET_MAX,
    ),
  }));
}

function benefitPayload(benefits: Array<{ title: string; body: string }>) {
  const titles: string[] = [];
  const bodies: string[] = [];
  benefits.forEach((benefit, index) => {
    const title = benefit.title.trim();
    const body = benefit.body.trim();
    if (!title && !body) return;
    titles.push(title || DEFAULT_INVITE_BENEFITS[index]?.title || `Benefit ${index + 1}`);
    bodies.push(body || DEFAULT_INVITE_BENEFITS[index]?.body || title);
  });
  return { page_bullet_titles: titles, page_bullets: bodies };
}

interface CreatorInviteDrawerProps {
  inviteId: string | null;
  onClose: () => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function toDateInputValue(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function fromDateInputValue(value: string): string | null {
  return value ? new Date(value).toISOString() : null;
}

function socialUrlForPlatform(platform: string, handle: string): string | null {
  const clean = handle
    .trim()
    .replace(/^@+/, '')
    .replace(/^\/+|\/+$/g, '');
  if (!clean) return null;
  switch (platform) {
    case 'x':
      return `https://x.com/${clean}`;
    case 'instagram':
      return `https://www.instagram.com/${clean}`;
    case 'tiktok':
      return `https://www.tiktok.com/@${clean}`;
    case 'youtube':
      return `https://www.youtube.com/@${clean}`;
    case 'facebook':
      return `https://www.facebook.com/${clean}`;
    case 'discord':
      return `https://discord.gg/${clean}`;
    default:
      return null;
  }
}

export function CreatorInviteDrawer({ inviteId, onClose }: CreatorInviteDrawerProps) {
  const open = Boolean(inviteId);
  const detailQuery = useCreatorInviteDetail(inviteId);
  const updateInvite = useUpdateCreatorInvite();
  const markSent = useMarkInviteSent();
  const invite = detailQuery.data?.invite ?? null;
  const [justSaved, setJustSaved] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const savedTimer = useRef<number | null>(null);
  const sentTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (savedTimer.current) window.clearTimeout(savedTimer.current);
      if (sentTimer.current) window.clearTimeout(sentTimer.current);
    };
  }, []);

  const flashSaved = () => {
    if (savedTimer.current) window.clearTimeout(savedTimer.current);
    setJustSaved(true);
    savedTimer.current = window.setTimeout(() => {
      setJustSaved(false);
      savedTimer.current = null;
    }, 1800);
  };

  const flashSent = () => {
    if (sentTimer.current) window.clearTimeout(sentTimer.current);
    setJustSent(true);
    sentTimer.current = window.setTimeout(() => {
      setJustSent(false);
      sentTimer.current = null;
    }, 1800);
  };

  return (
    <Sheet open={open} onOpenChange={(next) => (next ? null : onClose())}>
      <SheetContent className="w-[720px] max-w-full overflow-y-auto sm:max-w-[720px]">
        {detailQuery.isLoading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
        ) : detailQuery.isError ? (
          <div className="py-12 text-center text-sm text-destructive">Failed to load invite.</div>
        ) : invite ? (
          <>
            <SheetHeader>
              <SheetTitle>{invite.display_name ?? invite.handle ?? 'Creator invite'}</SheetTitle>
              <SheetDescription>
                {invite.status} · {stageLabel(invite.pipeline_stage)} · created{' '}
                {formatDate(invite.created_at)}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-5 text-sm">
              <CreatorMvpNote />

              <section className="rounded-md border bg-background p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div>
                    <div className="font-medium">Claim link</div>
                    <div className="text-xs text-muted-foreground">
                      Slug: {invite.claim_slug ?? invite.token}
                    </div>
                    <div className="break-all font-mono text-xs text-muted-foreground">
                      {invite.claim_url}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigator.clipboard?.writeText(invite.claim_url)}
                  >
                    Copy
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge>{stageLabel(invite.pipeline_stage)}</Badge>
                  {invite.outreach_channel ? (
                    <Badge variant="outline">
                      Sent on {channelLabel(invite.outreach_channel)}
                    </Badge>
                  ) : null}
                  {invite.last_contacted_at ? (
                    <Badge variant="outline">
                      Last contacted {formatDate(invite.last_contacted_at)}
                    </Badge>
                  ) : null}
                  {invite.claim_email_mismatch ? (
                    <Badge variant="destructive">Email mismatch</Badge>
                  ) : null}
                  {invite.claimed_at ? (
                    <Badge>Claimed {formatDate(invite.claimed_at)}</Badge>
                  ) : null}
                </div>
              </section>

              {invite.status === 'claimed' ? (
                <section className="rounded-md border bg-muted/30 p-3">
                  <div className="font-medium">Acquisition history</div>
                  <p className="mt-1 text-muted-foreground">
                    This invite has been claimed and is read-only here. Continue reviewing the
                    creator profile from the creator row.
                  </p>
                  <div className="mt-2 text-xs text-muted-foreground">
                    Claimed email: {invite.claimed_email ?? '—'}
                  </div>
                </section>
              ) : (
                <>
                  <OutreachCopySection invite={invite} />
                  <InviteEditor
                    invite={invite}
                    saving={updateInvite.isPending}
                    marking={markSent.isPending}
                    justSaved={justSaved}
                    justSent={justSent}
                    onSave={(body) =>
                      updateInvite.mutate({ id: invite.id, body }, { onSuccess: flashSaved })
                    }
                    onMarkSent={(channel) =>
                      markSent.mutate({ id: invite.id, channel }, { onSuccess: flashSent })
                    }
                  />
                </>
              )}
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function stageLabel(stage: CreatorInvite['pipeline_stage']): string {
  switch (stage) {
    case 'incomplete':
      return 'Setup incomplete';
    case 'ready_to_submit':
      return 'Ready to submit';
    case 'in_review':
      return 'In review';
    default:
      return stage.charAt(0).toUpperCase() + stage.slice(1);
  }
}

const CHANNEL_LABELS: Record<string, string> = {
  x: 'X',
  email: 'Email',
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
  other: 'Other',
};

function channelLabel(channel: string): string {
  return CHANNEL_LABELS[channel] ?? channel.charAt(0).toUpperCase() + channel.slice(1);
}

function InviteEditor({
  invite,
  saving,
  marking,
  justSaved,
  justSent,
  onSave,
  onMarkSent,
}: {
  invite: CreatorInvite;
  saving: boolean;
  marking: boolean;
  justSaved: boolean;
  justSent: boolean;
  onSave: (body: Partial<CreatorInvite>) => void;
  onMarkSent: (channel: OutreachChannel) => void;
}) {
  const [sendChannel, setSendChannel] = useState<OutreachChannel>(
    (invite.outreach_channel as OutreachChannel | null) ?? 'x',
  );
  const [draft, setDraft] = useState({
    email: invite.email ?? '',
    desired_slug: invite.desired_slug ?? '',
    display_name: invite.display_name ?? '',
    platform: invite.platform ?? 'x',
    handle: invite.handle ?? '',
    pitch: invite.pitch ?? '',
    page_headline: (invite.page_headline ?? '').slice(0, INVITE_HEADLINE_MAX),
    page_subtitle: (invite.page_subtitle ?? '').slice(0, INVITE_SUBTITLE_MAX),
    page_cta: (invite.page_cta ?? '').slice(0, INVITE_CTA_MAX),
    page_benefits: benefitsFromParts(invite.page_bullet_titles, invite.page_bullets),
    outreach_stage: invite.outreach_stage,
    last_contacted_at: toDateInputValue(invite.last_contacted_at),
    next_follow_up_at: toDateInputValue(invite.next_follow_up_at),
    outreach_note: invite.outreach_note ?? '',
  });

  useEffect(() => {
    setDraft({
      email: invite.email ?? '',
      desired_slug: invite.desired_slug ?? '',
      display_name: invite.display_name ?? '',
      platform: invite.platform ?? 'x',
      handle: invite.handle ?? '',
      pitch: invite.pitch ?? '',
      page_headline: (invite.page_headline ?? '').slice(0, INVITE_HEADLINE_MAX),
      page_subtitle: (invite.page_subtitle ?? '').slice(0, INVITE_SUBTITLE_MAX),
      page_cta: (invite.page_cta ?? '').slice(0, INVITE_CTA_MAX),
      page_benefits: benefitsFromParts(invite.page_bullet_titles, invite.page_bullets),
      outreach_stage: invite.outreach_stage,
      last_contacted_at: toDateInputValue(invite.last_contacted_at),
      next_follow_up_at: toDateInputValue(invite.next_follow_up_at),
      outreach_note: invite.outreach_note ?? '',
    });
  }, [invite.id, invite.updated_at]);

  const SUCCESS_GREEN = 'bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-700';
  const MARK_SENT_BASELINE =
    'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100';

  return (
    <section className="space-y-3 rounded-md border bg-background p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-medium">Lead and invite page</div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
            value={sendChannel}
            onChange={(e) => setSendChannel(e.target.value as OutreachChannel)}
            aria-label="Outreach channel"
          >
            <option value="x">X (Twitter) DM</option>
            <option value="email">Email</option>
            <option value="telegram">Telegram</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="other">Other</option>
          </select>
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.open(invite.claim_url, '_blank', 'noopener,noreferrer')}
            title="Open the public claim page in a new tab"
          >
            Preview claim page
          </Button>
          <Button
            size="sm"
            variant="outline"
            className={justSent ? SUCCESS_GREEN : MARK_SENT_BASELINE}
            onClick={() => onMarkSent(sendChannel)}
            disabled={marking}
          >
            {marking ? 'Marking…' : justSent ? '✓ Marked sent' : 'Mark sent'}
          </Button>
          <Button
            size="sm"
            className={justSaved ? SUCCESS_GREEN : ''}
            // Slug + handle are submit-critical. Save stays disabled until both
            // are present so an admin can't blank a previously valid invite and
            // wedge the creator at submit-review time (agent-arena#302).
            disabled={saving || !draft.desired_slug.trim() || !draft.handle.trim()}
            onClick={() => {
              const benefits = benefitPayload(draft.page_benefits);
              onSave({
                email: draft.email.trim() || null,
                // Required fields: send the trimmed value, never null. Backend
                // schema strips `.nullable()` on these so null would 400.
                desired_slug: draft.desired_slug.trim(),
                display_name: draft.display_name.trim() || null,
                platform: draft.platform,
                handle: draft.handle.trim(),
                social_url: socialUrlForPlatform(draft.platform, draft.handle),
                pitch: draft.pitch.trim() || null,
                page_headline: draft.page_headline.trim() || null,
                page_subtitle: draft.page_subtitle.trim() || null,
                page_cta: draft.page_cta.trim() || null,
                page_bullet_titles: benefits.page_bullet_titles,
                page_bullets: benefits.page_bullets,
                outreach_stage: draft.outreach_stage,
                last_contacted_at: fromDateInputValue(draft.last_contacted_at),
                next_follow_up_at: fromDateInputValue(draft.next_follow_up_at),
                outreach_note: draft.outreach_note.trim() || null,
              });
            }}
          >
            {saving ? 'Saving…' : justSaved ? '✓ Saved' : 'Save changes'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Input
          value={draft.display_name}
          placeholder="Display name"
          onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
        />
        <Input
          value={draft.email}
          placeholder="creator@example.com"
          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
        />
        <Input
          required
          value={draft.desired_slug}
          placeholder="desired-slug *"
          onChange={(e) => setDraft({ ...draft, desired_slug: e.target.value })}
        />
        <select
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={draft.outreach_stage}
          onChange={(e) =>
            setDraft({
              ...draft,
              outreach_stage: e.target.value as CreatorInvite['outreach_stage'],
            })
          }
        >
          <option value="draft">Draft</option>
          <option value="invited">Invited</option>
        </select>
        <select
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={draft.platform}
          onChange={(e) => setDraft({ ...draft, platform: e.target.value })}
        >
          <option value="x">X</option>
          <option value="instagram">Instagram</option>
          <option value="tiktok">TikTok</option>
          <option value="youtube">YouTube</option>
          <option value="facebook">Facebook</option>
          <option value="discord">Discord</option>
        </select>
        <Input
          required
          value={draft.handle}
          placeholder="@handle *"
          onChange={(e) => setDraft({ ...draft, handle: e.target.value })}
        />
        <Input
          value={socialUrlForPlatform(draft.platform, draft.handle) ?? invite.social_url ?? ''}
          placeholder="Derived social profile URL"
          disabled
        />
        <Input
          value={draft.page_headline}
          placeholder="Claim page headline"
          maxLength={INVITE_HEADLINE_MAX}
          onChange={(e) => setDraft({ ...draft, page_headline: e.target.value })}
        />
        <Input
          value={draft.page_cta}
          placeholder="Claim page CTA"
          maxLength={INVITE_CTA_MAX}
          onChange={(e) => setDraft({ ...draft, page_cta: e.target.value })}
        />
        <Input
          type="datetime-local"
          value={draft.last_contacted_at}
          onChange={(e) => setDraft({ ...draft, last_contacted_at: e.target.value })}
        />
        <Input
          type="datetime-local"
          value={draft.next_follow_up_at}
          onChange={(e) => setDraft({ ...draft, next_follow_up_at: e.target.value })}
        />
      </div>
      <textarea
        className="min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        placeholder="Claim page subtitle"
        maxLength={INVITE_SUBTITLE_MAX}
        value={draft.page_subtitle}
        onChange={(e) => setDraft({ ...draft, page_subtitle: e.target.value })}
      />
      <div className="grid gap-3 lg:grid-cols-3">
        {draft.page_benefits.map((benefit, index) => (
          <div key={index} className="rounded-md border bg-muted/20 p-3">
            <div className="mb-2 text-xs font-medium text-muted-foreground">
              Benefit {index + 1}
            </div>
            <Input
              placeholder="Benefit title"
              maxLength={INVITE_BULLET_TITLE_MAX}
              value={benefit.title}
              onChange={(e) =>
                setDraft((current) => ({
                  ...current,
                  page_benefits: current.page_benefits.map((item, itemIndex) =>
                    itemIndex === index ? { ...item, title: e.target.value } : item,
                  ),
                }))
              }
            />
            <textarea
              className="mt-2 min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Benefit content"
              maxLength={INVITE_BULLET_MAX}
              value={benefit.body}
              onChange={(e) =>
                setDraft((current) => ({
                  ...current,
                  page_benefits: current.page_benefits.map((item, itemIndex) =>
                    itemIndex === index ? { ...item, body: e.target.value } : item,
                  ),
                }))
              }
            />
          </div>
        ))}
      </div>
      <textarea
        className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        placeholder="Internal outreach note"
        value={draft.outreach_note}
        onChange={(e) => setDraft({ ...draft, outreach_note: e.target.value })}
      />
      <textarea
        className="min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        placeholder="Creator pitch / internal context"
        value={draft.pitch}
        onChange={(e) => setDraft({ ...draft, pitch: e.target.value })}
      />
    </section>
  );
}

/**
 * Per-channel outreach copy generator. Renders four cards (X DM, Email,
 * Telegram, WhatsApp) with editable templates pre-filled with the invite's
 * `display_name` and `claim_url`. Templates are local-state only — not
 * persisted to the backend in this iteration.
 */
function OutreachCopySection({ invite }: { invite: CreatorInvite }) {
  const displayName = (invite.display_name ?? invite.handle ?? 'there').trim() || 'there';
  const claimUrl = invite.claim_url;

  // Default templates with `{{display_name}}` and `{{claim_url}}` placeholders
  // baked in. Each is short, paste-ready, and informal — tone matches what we
  // would send a creator on a cold DM.
  const defaults = useMemo(
    () => ({
      xDm: interpolate(
        `Hey {{display_name}} — built you a creator page on Fantopy.ai (AI football managers + fan competitions). One-tap to claim: {{claim_url}}`,
        displayName,
        claimUrl,
      ),
      emailSubject: interpolate(
        `Your Fantopy creator page is ready, {{display_name}}`,
        displayName,
        claimUrl,
      ),
      emailBody: interpolate(
        `Hi {{display_name}},\n\nWe pre-built a creator page for you on Fantopy.ai — it's an AI football manager game where fans join your competition, build a squad, and get an AI manager that argues, helps, and reacts through the World Cup.\n\nClaim it (one tap, no setup): {{claim_url}}\n\nHappy to walk you through if useful — just reply here.\n\n— Fantopy`,
        displayName,
        claimUrl,
      ),
      telegram: interpolate(
        `Hey {{display_name}} — pre-built you a creator page on Fantopy.ai (AI football managers + fan competitions). Claim it here: {{claim_url}}`,
        displayName,
        claimUrl,
      ),
      whatsapp: interpolate(
        `Hi {{display_name}} — Fantopy here. Built you a creator page (AI football managers + fan competitions). Claim it: {{claim_url}}`,
        displayName,
        claimUrl,
      ),
    }),
    [displayName, claimUrl],
  );

  const [xDm, setXDm] = useState(defaults.xDm);
  const [emailSubject, setEmailSubject] = useState(defaults.emailSubject);
  const [emailBody, setEmailBody] = useState(defaults.emailBody);
  const [telegram, setTelegram] = useState(defaults.telegram);
  const [whatsapp, setWhatsapp] = useState(defaults.whatsapp);

  // Re-seed when the invite swaps under us (e.g. drawer switches invites).
  useEffect(() => {
    setXDm(defaults.xDm);
    setEmailSubject(defaults.emailSubject);
    setEmailBody(defaults.emailBody);
    setTelegram(defaults.telegram);
    setWhatsapp(defaults.whatsapp);
  }, [
    defaults.xDm,
    defaults.emailSubject,
    defaults.emailBody,
    defaults.telegram,
    defaults.whatsapp,
  ]);

  return (
    <section className="space-y-3 rounded-md border bg-background p-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium">Outreach copy</div>
          <p className="text-xs text-muted-foreground">
            Edit the message, copy it, then paste into the channel. Edits are not saved.
          </p>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <CopyCard label="X (Twitter) DM" value={xDm} onChange={setXDm} />
        <CopyCard label="Telegram" value={telegram} onChange={setTelegram} />
        <CopyCard label="WhatsApp" value={whatsapp} onChange={setWhatsapp} />
        <div className="space-y-2 rounded-md border bg-muted/20 p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Email
          </div>
          <CopyField
            label="Subject"
            value={emailSubject}
            onChange={setEmailSubject}
            singleLine
          />
          <CopyField label="Body" value={emailBody} onChange={setEmailBody} />
        </div>
      </div>
    </section>
  );
}

function interpolate(template: string, displayName: string, claimUrl: string): string {
  return template
    .replaceAll('{{display_name}}', displayName)
    .replaceAll('{{claim_url}}', claimUrl);
}

function CopyCard({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="space-y-2 rounded-md border bg-muted/20 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <CopyField value={value} onChange={onChange} />
    </div>
  );
}

function CopyField({
  label,
  value,
  onChange,
  singleLine,
}: {
  label?: string;
  value: string;
  onChange: (next: string) => void;
  singleLine?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1_500);
    } catch {
      // Older browsers / blocked contexts: silently no-op; user can still
      // hand-select the textarea contents.
      setCopied(false);
    }
  };

  return (
    <div className="space-y-1">
      {label ? (
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
      ) : null}
      {singleLine ? (
        <Input value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <textarea
          className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      <div className="flex items-center justify-end gap-2">
        {copied ? (
          <span className="text-xs text-primary" aria-live="polite">
            Copied!
          </span>
        ) : null}
        <Button size="sm" variant="outline" type="button" onClick={handleCopy}>
          Copy
        </Button>
      </div>
    </div>
  );
}
